import { useState, useEffect, useCallback } from 'react';
import { getJson } from '../global/func';

/**
 * How a box is doing, for the status line at the top of the home screen.
 *
 * The device list cannot answer this. It carries last_checkin but not the age
 * of it, and the age is the part that matters -- the server computes it with
 * TIMESTAMPDIFF because this host runs PHP in UTC and MySQL in Eastern time and
 * a phone adds a third zone, so a bare timestamp means nothing without knowing
 * which of the three it is in. The home screen read a field that was never
 * there and reported every box as "status unknown".
 *
 * The list is also written at sign-in and kept in local storage, so a box that
 * started reporting afterwards would look silent until the customer signed out
 * and back in.
 *
 * This asks the endpoint that does know. Once a minute is plenty: a box checks
 * in every fifteen.
 *
 * The same answer carries what the box is reciting, which moves on a different
 * clock entirely -- so the caller can ask for a shorter interval while the
 * recitation is playing and go back to a minute when it stops.
 */
export const useDeviceHealth = (deviceKey, user, intervalMs = 60 * 1000) => {
  const [health, setHealth] = useState(null);
  // Owner or member, as the server sees it right now.
  //
  // The device list carries a role too, but that list is written at sign-in and
  // kept in local storage -- so somebody signed in before sharing existed holds
  // a list with no role on it at all, and the app would read that as "owner" and
  // offer them controls the server refuses. This is the same answer from the
  // same request the app is already making, and it cannot be stale.
  const [role, setRole] = useState(null);

  const refresh = useCallback(async () => {
    if (!deviceKey || !user?.id) return;

    const url = encodeURI(import.meta.env.REACT_APP_API_SERVER_URL + '/device/command/');
    const result = await getJson(url, {
      DEVICE_KEY: deviceKey,
      USER_ID: user.id,
      AUTH_KEY: user.auth_key,
    });

    // This endpoint answers {commands, health, ...} directly rather than
    // wrapping it in a data member, which is why it is read with getJson.
    if (result?.ok && result.data?.health) setHealth(result.data.health);
    if (result?.ok && result.data?.role) setRole(result.data.role);
  }, [deviceKey, user?.id, user?.auth_key]);

  useEffect(() => {
    refresh();
    const timer = setInterval(refresh, intervalMs);
    return () => clearInterval(timer);
  }, [refresh, intervalMs]);

  return { health, role };
};
